import {
  Autocomplete,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { useSectorManagersIndex } from "queries/sector_managers";
import { useShopBrandsIndex } from "queries/shop_brands";
import { useTranslation } from "react-i18next";
import { Feature, useAuthorization } from "user";
import { FiltersState, UpdatedAtFilterOption, completionRateFilterOption } from "./useFilters";

interface Props {
  filters: FiltersState;
  setFilters: React.Dispatch<React.SetStateAction<FiltersState>>;
}

const Filters = ({ filters, setFilters }: Props) => {
  const { t } = useTranslation();
  const { data: sectorManagers = [] } = useSectorManagersIndex();
  const { data: shopBrands = [] } = useShopBrandsIndex();
  const hasAccess = useAuthorization();

  const lastUpdatedAtOptions = [UpdatedAtFilterOption.M1, UpdatedAtFilterOption.M1M3, UpdatedAtFilterOption.M3];
  const completionRateOptions = [completionRateFilterOption.T100, completionRateFilterOption.T50, completionRateFilterOption.T0];

  const sectorManagerOptions = sectorManagers.map((sectorManager) => ({
    label: sectorManager.fullName,
    employeeId: sectorManager.employeeId,
  }));

  return (
    <>
      <Typography variant="h6">{t("shop.index.filters")}</Typography>
      <Paper sx={{ mt: 3 }}>
        <Grid container p={2} spacing={2} pt={0}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                label={t("shop.index.search")}
                type="search"
                onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                InputProps={{
                  startAdornment: <SearchIcon></SearchIcon>,
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>{t("shop.index.shopBrand")}</InputLabel>
              <Select
                fullWidth
                label={t("shop.index.shopBrand")}
                onChange={(e) =>
                  setFilters({ ...filters, brandId: e.target.value !== "" ? (e.target.value as number) : null })
                }
                value={filters.brandId || ""}
              >
                <MenuItem key="none" value="">
                  {t("shop.index.shopBrandSelect")}
                </MenuItem>
                {shopBrands.map((brand) => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>{t("shop.index.lastUpdatedAt")}</InputLabel>
              <Select
                fullWidth
                label={t("shop.index.lastUpdatedAt")}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    updatedAt: e.target.value !== "" ? (e.target.value as UpdatedAtFilterOption) : null,
                  })
                }
                value={filters.updatedAt || ""}
              >
                <MenuItem key="none" value="">
                  {t("shop.index.lastUpdatedAtSelect")}
                </MenuItem>
                {lastUpdatedAtOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(`shop.index.lastUpdatedAt${option}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>{t("shop.index.completionRate")}</InputLabel>
              <Select
                fullWidth
                label={t("shop.index.completionRate")}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    completionRate: e.target.value !== "" ? (e.target.value as completionRateFilterOption) : null,
                  })
                }
                value={filters.completionRate || ""}
              >
                <MenuItem key="none" value="">
                  {t("shop.index.completionRateSelect")}
                </MenuItem>
                {completionRateOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(`shop.index.completionRate${option}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {hasAccess(Feature.ShopFiltersSectorManagerFilter) && (
            <Grid item xs={12} sm={3}>
              <Autocomplete
                clearText={t("a11y.clear")}
                openText={t("a11y.open")}
                closeText={t("a11y.close")}
                disablePortal
                options={sectorManagerOptions}
                renderInput={(params) => <TextField {...params} label={t("shop.index.sectorManager")} />}
                value={sectorManagerOptions.find((option) => option.employeeId === filters.sectorManagerId) || null}
                onChange={(e: any, value) => setFilters({ ...filters, sectorManagerId: value?.employeeId || null })}
              />
            </Grid>
          )}
        </Grid>
      </Paper>
    </>
  );
};

export default Filters;
